import type { AuthForHAR } from '@readme/oas-to-har/lib/types';
import type { HarRequest } from '@readme/oas-to-snippet/types';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';

import configureSecurity from '@readme/oas-to-har/lib/configure-security';
import oasToSnippet from '@readme/oas-to-snippet';
import httpsnippetClientAPIPlugin from 'httpsnippet-client-api';
import { matchesMimeType } from 'oas/utils';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';
import useDereference from '@core/hooks/useDereference';
import type { ReferenceStore } from '@core/store';
import { useProjectStore, useReferenceStore } from '@core/store';
import type { LibraryTarget } from '@core/store/Reference/Language/types';

import APIExecutor from '@ui/API/Executor';
import Badge from '@ui/Badge';
import Button from '@ui/Button';
import CodeInput from '@ui/CodeInput';
import CodeSnippet from '@ui/CodeSnippet';
import CopyToClipboard from '@ui/CopyToClipboard';
import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import InputGroup from '@ui/InputGroup';
import Menu, { MenuHeader, MenuItem } from '@ui/Menu';
import Tooltip from '@ui/Tooltip';

import classes from './style.module.scss';
import useExternalSnippet from './useExternalSnippet';

export interface APIRequestProps {
  /** Flag that turns on the JSON Editor on the Dash */
  allowApiExplorerJsonEditor?: boolean;

  /** The current OpenAPI file */
  apiDefinition: Oas;

  /**
   * This is a keyed object containing authentication credentials for the operation. The keys for this object should
   * match up with the `securityScheme` on the operation you're accessing, and its value should either be a `string`,
   * or an `object` containing `user` and/or `pass` (for Basic auth schemes).
   *
   * This data can be obtained from the `ReferenceStore`.
   */
  auth?: AuthForHAR;

  /**
   * A HAR file which is used during code snippet generation. If provided this will override the
   * accompanying OAS.
   *
   * This prop specifically addresses the use case where a user is viewing a particular OAS
   * operation and then clicks on and particular metric and wants the code snippet to update to
   * reflect the information associated with that metric's HAR log.
   *
   * @see {@link http://www.softwareishard.com/blog/har-12-spec/}
   */
  har?: HarRequest | null;

  /** Determines if the current Operation is a Webhook class */
  isWebhook?: boolean;

  /** Triggered when errors occur in the "Try It!" button flow. */
  onError?: (message: string) => void;

  /** Triggered when the response comes back from the "Try It!" button. */
  onResponse?: (parsedRes: unknown, res: unknown, har: unknown) => void;

  /** The current OpenAPI operation */
  operation: Operation;

  /** Options passed to CodeMirror */
  options?: { foldGutter?: boolean; readOnly?: boolean };

  /**  Does the spec enable the try-it-now section */
  requestsEnabled?: boolean;

  /**
   * These are props that are only used in Styleguidist previews.
   * Everything here is normally rendered via `ReferenceStore`.
   */
  styleguidist?: {
    availableLanguageLibraries: ReferenceStore['language']['availableLanguageLibraries'];
    getCurrentFormData?: ReferenceStore['form']['getCurrentFormData'];
    language?: ReferenceStore['language']['language'];
    languageLibrary: ReferenceStore['language']['languageLibrary'];
    rawJsonEditorEnabled?: ReferenceStore['form']['rawJsonEditor']['isEnabled'];
    rawJsonEditorStringifiedJson?: ReferenceStore['form']['rawJsonEditor']['stringifiedJson'];
    rawJsonEditorValidationError?: ReferenceStore['form']['rawJsonEditor']['validationError'];
    resetForm?: ReferenceStore['form']['reset'];
    schemaEditorData?: ReferenceStore['form']['schemaEditor']['data'];
    schemaEditorFiles?: ReferenceStore['form']['schemaEditor']['files'];
    selectedRequestExampleKey?: ReferenceStore['form']['selectedRequestExampleKey'];
    toggleJsonEditor?: ReferenceStore['form']['toggleJsonEditor'];
    updateLanguage?: ReferenceStore['language']['updateLanguage'];
    updateRawJsonFormData?: ReferenceStore['form']['updateRawJsonFormData'];
    updateRequestExample?: ReferenceStore['form']['updateRequestExample'];
  };

  /**
   * This URL parameter is only necessary when using the `node-simple` language and it should be an
   * addressable URL for your operation so it can be used within ReadMe's OpenAPI auto SDK package.
   *
   * @link https://www.npmjs.com/package/api
   */
  url?: string;
}

/**
 * Consume OAS + log + users auth
 * to attempt to replace any hashed values in the log with a user's
 * current auth
 * @returns har
 */
function overrideHarAuth(oas, operation, log, auth) {
  const apiDefinition = oas.getDefinition();
  const {
    log: {
      entries: [{ request: har }],
    },
  } = log;

  // Attempt to target authentication mechanisms for a particular OAS operation
  const securityRequirements = operation.getSecurity();
  if (securityRequirements?.length) {
    securityRequirements.forEach(schemes => {
      Object.keys(schemes).forEach(security => {
        const securityValue = configureSecurity(apiDefinition, auth, security);
        if (!securityValue) return;

        // Determine if HAR for API request includes a header that matches the defined auth
        const matchIndex = har[securityValue?.type].findIndex(
          v => v?.name?.toLowerCase() === securityValue?.value?.name?.toLowerCase(),
        );

        // If it does, hash the associated value and attempt to replace it with a user's JWT-passed
        // credentials via comparison
        if (matchIndex > -1) {
          const match = har[securityValue?.type][matchIndex];
          // This looks like a hashed header
          if (
            match.value?.startsWith('sha512-') &&
            match.value?.endsWith(`?${securityValue?.value?.value?.slice(-4)}`)
          ) {
            har[securityValue?.type][matchIndex].value = securityValue?.value?.value;
          }
        }
      });
    });
  }
  return log;
}

const APIRequest: React.FC<React.PropsWithChildren<APIRequestProps>> = React.memo(function APIRequest({
  apiDefinition,
  auth = {},
  allowApiExplorerJsonEditor,
  har,
  isWebhook,
  onError = () => {},
  onResponse = () => {},
  operation,
  requestsEnabled,
  url,
  ...props
}: APIRequestProps) {
  const { t } = useTranslation();

  const [
    availableLanguageLibraries,
    language,
    languageLibrary,
    updateLanguage,
    isJsonEditorEnabled,
    stringifiedJsonFormData,
    jsonFormValidationError,
    resetFormState,
    selectedExampleKey,
    toggleJsonEditor,
    onJsonFormChange,
    getCurrentFormData,
    files,
    updateRequestExample,
    formDataFromStore,
  ] = useReferenceStore(store => [
    props.styleguidist?.availableLanguageLibraries ?? store.language.availableLanguageLibraries ?? [],
    props.styleguidist?.language ?? store.language.language,
    // we can safely assume that `languageLibrary` is non-null since
    // this component is not ever rendering an AWS or .NET.
    props.styleguidist?.languageLibrary ?? (store.language.languageLibrary as LibraryTarget),
    props.styleguidist?.updateLanguage ?? store.language.updateLanguage,
    props.styleguidist?.rawJsonEditorEnabled ?? store.form.rawJsonEditor.isEnabled,
    props.styleguidist?.rawJsonEditorStringifiedJson ?? store.form.rawJsonEditor.stringifiedJson,
    props.styleguidist?.rawJsonEditorValidationError ?? store.form.rawJsonEditor.validationError,
    props.styleguidist?.resetForm ?? store.form.reset,
    props.styleguidist?.selectedRequestExampleKey ?? store.form.selectedRequestExampleKey,
    props.styleguidist?.toggleJsonEditor ?? store.form.toggleJsonEditor,
    props.styleguidist?.updateRawJsonFormData ?? store.form.updateRawJsonFormData,
    props.styleguidist?.getCurrentFormData ?? store.form.getCurrentFormData,
    props.styleguidist?.schemaEditorFiles ?? store.form.schemaEditor.files,
    props.styleguidist?.updateRequestExample ?? store.form.updateRequestExample,
    props.styleguidist?.schemaEditorData ?? store.form.schemaEditor.data,
  ]);

  const formData = useMemo(
    () => ({
      ...getCurrentFormData(),
      // Note: not sure why but for some reason we merged our form data object with the files object?
      // It doesn't appear to be something that `oas-to-snippet` even uses. This is holdover logic from here:
      // https://github.com/readmeio/readme/blob/0a3c3b43a5c9a9a1e42e9792b5e7fb8b4d571bd0/packages/react/src/routes/Reference/index.jsx#L631
      files,
    }),
    // we need `formDataFromStore` and `stringifiedJsonFormData` here so the formData variable
    // updates properly when the user updates the server variable input and raw JSON form
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, formDataFromStore, getCurrentFormData, stringifiedJsonFormData],
  );
  const isExternalSdkSnippetsEnabled = useProjectStore(s => s.data?.reference?.sdk_snippets?.external === 'active');

  const {
    code: codeExternalSnippet,
    install: installExternalSnippet,
    isLoading: isLoadingExternalSnippet,
    error: errorExternalSnippet,
  } = useExternalSnippet({
    apiDefinition,
    formData: structuredClone(formData),
    auth,
    operation,
    language,
    enabled: isExternalSdkSnippetsEnabled,
  });

  // This API definition should probably be hoisted up into an `OpenAPIContext` or something that
  // handles dereferencing awareness for us (instead of these components calling `useDereference`
  // themselves).
  const { isDereferenced } = useDereference(apiDefinition);

  const bem = useClassy(classes, 'RawEditor');
  const jsonSchema = operation.getParametersAsJSONSchema();
  const onlyBodyParams = !jsonSchema?.filter(schema => {
    return schema.type !== 'body';
  }).length;

  /**
   * These editor options do the following:
   *    - allow code folding, which defaults to edit mode, so we have to also specify readOnly mode.
   *    - disable codemirror's default tab-trap behavior, see RM-929
   */
  const options = useMemo(
    () => ({
      dark: true,
      extraKeys: { Tab: false, 'Shift-Tab': false },
      foldGutter: true,
      highlightMode: true,
      inputStyle: 'contenteditable' as const,
      readOnly: true,
      ...props.options,
    }),
    [props.options],
  );

  const examples = useMemo(() => {
    if (!isDereferenced) return {};

    // Grab the first of any json types that are available
    // TODO support other mime types
    const mediaTypeObject = operation
      .getRequestBodyExamples()
      .find(type => matchesMimeType.json(type.mediaType) || matchesMimeType.formUrlEncoded(type.mediaType));
    if (!mediaTypeObject) return {};

    return mediaTypeObject.examples
      .map(example => {
        return {
          ...example,
          // Non-keyed examples on schemas don't have summaries so we need to add one.
          // https://github.com/OAI/OpenAPI-Specification/blob/master/versions/3.0.3.md#schema-object
          summary: example?.summary || (isWebhook ? t('reference.payloadExample') : t('reference.requestExample')),
        };
      })
      .reduce((acc, example) => {
        if (example == null) {
          return acc;
        }

        acc[example.summary] = example.value;
        return acc;
      }, {});
  }, [isDereferenced, isWebhook, operation, t]);

  const hasCustomCodeSample =
    availableLanguageLibraries.some(library => !!library.snippet) && !isExternalSdkSnippetsEnabled;
  const { code, highlightMode, install } = useMemo(() => {
    if (languageLibrary.target === 'SDK') {
      const sdkSnippet = {
        highlightMode: languageLibrary.id[0] || 'shell',
        code: codeExternalSnippet || t('reference.sdkCodeEmpty'),
        install: installExternalSnippet || '',
      };
      if (errorExternalSnippet) {
        sdkSnippet.code = t('reference.sdkCodeError');
      }
      return sdkSnippet;
    }
    // If the current library has a custom-written code snippet, use that instead of an
    // auto-generated one.
    if (languageLibrary?.snippet) {
      return { ...languageLibrary.snippet, install: languageLibrary.install };
    }

    // We need to clone this otherwise oas-to-snippet will mess with the original object,
    // which will throw errors because this object is readonly because of zustand + immer
    const clonedHar = structuredClone(har);

    let generatedSnippet: ReturnType<typeof oasToSnippet>;
    try {
      const overriddenHar = clonedHar ? overrideHarAuth(apiDefinition, operation, clonedHar, auth) : clonedHar;
      /**
       * It's possible that the context-stored auth doesn't match the expected format of the OAS
       * we're rendering a snippet for and `oasToSnippet`, and the `api` snippet generator, may
       * throw an error in this case. Ref: RM-1323
       */
      generatedSnippet = oasToSnippet(
        apiDefinition,
        operation,
        // We need to clone this otherwise oas-to-snippet will mess with the original object,
        // which will throw errors because this object is readonly because of zustand + immer
        structuredClone(formData),
        auth,
        isWebhook ? 'json' : languageLibrary.id,
        {
          harOverride: overriddenHar,
          openapi: {
            registryIdentifier: url,
          },
          plugins: [httpsnippetClientAPIPlugin],
        },
      );
    } catch (e) {
      // Try again without the auth in context
      generatedSnippet = oasToSnippet(
        apiDefinition,
        operation,
        // We need to clone this otherwise oas-to-snippet will mess with the original object,
        // which will throw errors because this object is readonly because of zustand + immer
        structuredClone(formData),
        {},
        isWebhook ? 'json' : languageLibrary.id,
        {
          harOverride: clonedHar as HarRequest,
          openapi: {
            registryIdentifier: url,
          },
          plugins: [httpsnippetClientAPIPlugin],
        },
      );
    }

    return generatedSnippet;
  }, [
    languageLibrary.snippet,
    languageLibrary.target,
    languageLibrary.install,
    languageLibrary.id,
    har,
    codeExternalSnippet,
    installExternalSnippet,
    errorExternalSnippet,
    apiDefinition,
    operation,
    auth,
    formData,
    isWebhook,
    url,
    t,
  ]);

  const handleMenuItemClick = useCallback(
    key => {
      const requestBody = key === null ? undefined : examples[key];
      const isFormEncoded = matchesMimeType.formUrlEncoded(operation.contentType);
      updateRequestExample(key, requestBody, isFormEncoded);
    },
    [examples, operation.contentType, updateRequestExample],
  );

  const header = useMemo(() => {
    return (
      <>
        {availableLanguageLibraries.length > 1 && !!hasCustomCodeSample && (
          <Flex className={classes['APIRequest-example-tab']} tag="header">
            <Flex align="center" className={classes['APIRequest-example-tab-container']} justify="start">
              <InputGroup className={classes['APIRequest-example-inputgroup']} columnLayout="auto" size="sm">
                {availableLanguageLibraries.map(lib => (
                  <Button
                    key={lib.name}
                    className={`
                    ${classes['APIRequest-example-button']}
                    ${lib.name === languageLibrary.name ? classes['APIRequest-example-button_active'] : ''}
                  `}
                    kind="secondary"
                    onClick={() => {
                      updateLanguage(language, lib);
                    }}
                  >
                    {lib.name}
                  </Button>
                ))}
              </InputGroup>
            </Flex>
          </Flex>
        )}

        <Flex align="center" className={classes['APIRequest-header']} tag="header">
          {availableLanguageLibraries.length > 1 && !hasCustomCodeSample && (
            <>
              {!isWebhook && (
                <section className={classes['APIRequest-libraries']}>
                  <Dropdown clickInToClose sticky>
                    <Button ghost kind="contrast" size="xs" title="Toggle library" uppercase={false}>
                      {`${languageLibrary.name} ${t('reference.request')}`}
                      <Icon aria-hidden="true" name="chevron-down" />
                    </Button>
                    <Menu className={classes['APIRequest-menu']} theme="dark">
                      <MenuHeader className={`${classes['APIRequest-menu-header']}`}>
                        {t('reference.library')}
                      </MenuHeader>
                      {availableLanguageLibraries.map((curr, i) => (
                        <MenuItem
                          key={`${curr.target}-${i}`}
                          active={languageLibrary.id.join(':') === curr.id.join(':')}
                          aria-selected={languageLibrary.target === curr.target}
                          className={`${classes['APIRequest-menu-item']} ${classes['APIRequest-menu-item_example']}`}
                          onClick={() => {
                            return updateLanguage(language, curr);
                          }}
                          role="option"
                        >
                          {curr.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Dropdown>
                </section>
              )}
            </>
          )}

          {!!isWebhook && <span className={classes['APIRequest-webhook-header']}>Payload</span>}

          {((availableLanguageLibraries.length < 2 && !isWebhook) || !!hasCustomCodeSample) && (
            <span className={classes['APIRequest-single-library']}>{t('reference.request')}</span>
          )}

          {!hasCustomCodeSample && Boolean(Object.keys(examples).length) && (
            <Dropdown clickInToClose sticky>
              <Button className={classes['APIRequest-header-button']} ghost kind="minimum" size="xs" uppercase={false}>
                {selectedExampleKey ? (
                  <>
                    <span className={classes['APIRequest-header-button-text']}>{selectedExampleKey}</span>{' '}
                    <Badge className={classes['APIRequest-header-button-badge']}>{t('reference.example')}</Badge>
                  </>
                ) : (
                  <span>{t('reference.examples')}</span>
                )}
                <Icon aria-hidden="true" name="chevron-down" />
              </Button>
              <Menu className={classes['APIRequest-menu']} theme="dark">
                {Object.keys(examples).length > 1 && (
                  <MenuHeader className={classes['APIRequest-menu-header']}>
                    {t('reference.requestExamples')}
                  </MenuHeader>
                )}
                {Object.keys(examples).map(key => (
                  <MenuItem
                    key={key}
                    active={selectedExampleKey === key}
                    aria-selected={selectedExampleKey === key}
                    className={`${classes['APIRequest-menu-item']} ${classes['APIRequest-menu-item_example']}`}
                    onClick={() => handleMenuItemClick(key)}
                    role="option"
                  >
                    {key}
                  </MenuItem>
                ))}
              </Menu>
            </Dropdown>
          )}
        </Flex>

        {!!install && !isWebhook && (
          <section className={classes['APIRequest-instructions']}>
            <div className={`${classes['APIRequest-instructions-body']}`}>
              <input
                aria-label={t('reference.requestInstructions')}
                className={`${classes['APIRequest-instructions-input']}`}
                readOnly
                value={install}
              />
            </div>
          </section>
        )}
      </>
    );
  }, [
    availableLanguageLibraries,
    examples,
    handleMenuItemClick,
    hasCustomCodeSample,
    install,
    isWebhook,
    language,
    languageLibrary.id,
    languageLibrary.name,
    languageLibrary.target,
    selectedExampleKey,
    updateLanguage,
    t,
  ]);

  const rawJsonFooter = useMemo(() => {
    return (
      <>
        {!!jsonFormValidationError && (
          <Flex className={classes['RawEditor-footer']} justify="end" tag="footer">
            <Flex align="center" className={bem('-errors')} gap="xs">
              <div className={bem('-errors-text')}>{t('reference.invalidJSON')}</div>
            </Flex>
          </Flex>
        )}
        <button
          className={`Button Button_xs ${classes['APIRequest-footer-toggle-button']}`}
          onClick={() => resetFormState({ jsonForm: true, requestExample: true })}
          type="button"
        >
          {t('reference.resetBody')}
        </button>
      </>
    );
  }, [bem, resetFormState, jsonFormValidationError, t]);

  const rawJsonHeader = useMemo(() => {
    return <Flex align="center" className={bem('-header')} justify="end" />;
  }, [bem]);

  const footer = useMemo(() => {
    return code ? (
      <>
        {!!allowApiExplorerJsonEditor && !!isJsonEditorEnabled && !!onlyBodyParams && !isWebhook && (
          <Flex align="stretch" layout="col" tag="section">
            <CodeInput
              className={bem('-input')}
              footer={rawJsonFooter}
              header={rawJsonHeader}
              initialValue={stringifiedJsonFormData}
              isDarkMode
              language={'json'}
              onChange={onJsonFormChange}
              options={{ lineNumbers: false }}
              theme="material-palenight"
            />
          </Flex>
        )}

        <footer className={classes['APIRequest-footer']}>
          <Flex align="center" gap="xs">
            <CopyToClipboard
              aria-label={t('copyToClipboard.copyFull')}
              className={`rm-APIRequest-copy Button Button_sm ${classes['APIRequest-footer-toggle-button']}`}
              shift="end"
              text={code}
            />
            {!!allowApiExplorerJsonEditor && !!onlyBodyParams && !isWebhook && !hasCustomCodeSample && (
              <Tooltip content={t('reference.jsonEditorLabel')} placement="bottom">
                <button
                  aria-label={t('reference.jsonEditorAriaLabel')}
                  className={`rm-APIRequest-json Button Button_sm ${classes['APIRequest-footer-toggle-button']}`}
                  onClick={toggleJsonEditor}
                  type="button"
                >
                  <Icon aria-hidden name="json" />
                </button>
              </Tooltip>
            )}
            {!hasCustomCodeSample && selectedExampleKey !== null && (
              <Tooltip content={t('reference.clearExample')} placement="bottom">
                <button
                  aria-label={t('reference.clearExample')}
                  className={`rm-APIRequest-clear Button Button_sm ${classes['APIRequest-footer-toggle-button']}`}
                  onClick={() => {
                    handleMenuItemClick(null);
                    if (isJsonEditorEnabled) resetFormState({ jsonForm: true });
                  }}
                  type="button"
                >
                  <Icon name="rotate-ccw" />
                </button>
              </Tooltip>
            )}
          </Flex>

          {!isWebhook && (
            <APIExecutor
              apiDefinition={apiDefinition}
              auth={auth}
              formData={formData}
              harOverride={har}
              onAuthFailure={onError}
              onResponse={onResponse}
              operation={operation}
              requestsEnabled={requestsEnabled}
            />
          )}
        </footer>
      </>
    ) : (
      false
    );
  }, [
    allowApiExplorerJsonEditor,
    apiDefinition,
    auth,
    bem,
    code,
    formData,
    handleMenuItemClick,
    hasCustomCodeSample,
    har,
    isWebhook,
    isJsonEditorEnabled,
    onError,
    onJsonFormChange,
    onlyBodyParams,
    onResponse,
    operation,
    rawJsonFooter,
    rawJsonHeader,
    requestsEnabled,
    resetFormState,
    selectedExampleKey,
    stringifiedJsonFormData,
    toggleJsonEditor,
    t,
  ]);

  if (!isDereferenced || !code || !highlightMode) {
    return null;
  }

  if (isWebhook) {
    return (
      <>
        {!selectedExampleKey && (
          <section className={classes.WebhookRequest}>
            {header}
            <Flex align="center" className={classes['WebhookRequest-empty']} gap="md" layout="col">
              <div>{t('reference.examplePrompt')}</div>
            </Flex>
          </section>
        )}
        {!!selectedExampleKey && (
          <CodeSnippet
            className={`${classes.APIRequest} rm-PlaygroundRequest`}
            code={code}
            footer={footer || undefined}
            header={header}
            isLoading={!!isExternalSdkSnippetsEnabled && isLoadingExternalSnippet}
            language={highlightMode}
            options={options}
          />
        )}
      </>
    );
  }

  return (
    <CodeSnippet
      className={
        isJsonEditorEnabled ? `${classes.APIRequest} rm-RawEditorRequest` : `${classes.APIRequest} rm-PlaygroundRequest`
      }
      code={code}
      footer={footer || undefined}
      header={header}
      isLoading={!!isExternalSdkSnippetsEnabled && isLoadingExternalSnippet}
      language={highlightMode}
      options={options}
    />
  );
});

export { overrideHarAuth };
export default APIRequest;
